import clsx from 'clsx';
import React, { useId, useRef } from 'react';
import {
  ADDON_KEYS,
  ADDON_META,
  type AddonSelection,
  type PlanPricing,
  formatMinutes,
  formatMoney,
  formatRate,
  planHue,
} from '../../shared/plan';
import { Reason } from './Feedback';
import { Tag } from './Status';
import { HUE_FILL, type Hue } from './hue';
import { IconCheck } from './icons';

/**
 * PlanChooser: a selectable *list* of plans — not cards. One row per plan:
 * swatch · name · tag · description, and the four numbers that matter in
 * fixed columns so plans compare down the page. The selected row opens to
 * show usage pricing, add-on checkboxes and the feature list.
 *
 * Semantics: `role="radiogroup"` with roving tabindex; arrows move, Space /
 * Enter select. Add-ons live outside the radio element so nested controls
 * stay legal. The chooser never submits — the Receipt beside it owns the
 * primary.
 */
export interface PlanOption {
  /** Radio value: plan code, or an offer token when the option is an offer. */
  code: string;
  name: string;
  description?: React.ReactNode;
  pricing: PlanPricing;
  /** Defaults to `planHue(code)`; pass when the option's code is a token. */
  hue?: Hue;
  /** Quiet metadata beside the name: Current · Scheduled · Custom pricing. */
  tag?: { label: string; tone: React.ComponentProps<typeof Tag>['tone'] };
  features?: string[];
  /** When set, add-ons render checked/unchecked but cannot be changed. */
  addonsLocked?: string;
}

export const PlanChooser: React.FC<{
  options: PlanOption[];
  value: string | null;
  onChange: (code: string) => void;
  addons: AddonSelection;
  onAddonsChange: (sel: AddonSelection) => void;
  showAddons?: boolean;
  className?: string;
  'aria-label'?: string;
}> = ({
  options,
  value,
  onChange,
  addons,
  onAddonsChange,
  showAddons = true,
  className,
  ...rest
}) => {
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const groupId = useId();

  const move = (from: number, delta: number) => {
    if (options.length === 0) return;
    const next = (from + delta + options.length) % options.length;
    onChange(options[next].code);
    rowRefs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label'] ?? 'Plans'}
      className={clsx('border-y border-v2-line', className)}
    >
      {options.map((o, i) => {
        const selected = o.code === value;
        const hue = o.hue ?? planHue(o.code);
        const p = o.pricing;
        const labelId = `${groupId}-${i}`;
        return (
          <div
            key={o.code}
            className={clsx(
              'border-b border-v2-line last:border-b-0',
              selected && 'bg-v2-accent/[0.04]',
            )}
          >
            <div
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              role="radio"
              aria-checked={selected}
              aria-labelledby={labelId}
              tabIndex={selected || (value == null && i === 0) ? 0 : -1}
              onClick={() => onChange(o.code)}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  onChange(o.code);
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(i, 1);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(i, -1);
                }
              }}
              className={clsx(
                'grid cursor-pointer grid-cols-[minmax(0,1fr)_20px] items-start gap-x-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto_20px]',
                !selected && 'hover:bg-v2-sunken/60',
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <PlanSwatch hue={hue} />
                  <span
                    id={labelId}
                    className="text-v2-14 font-semibold tracking-[-0.01em] text-v2-fg"
                  >
                    {o.name}
                  </span>
                  {o.tag && <Tag tone={o.tag.tone}>{o.tag.label}</Tag>}
                </div>
                {o.description && (
                  <p className="mt-0.5 max-w-[56ch] text-v2-12 leading-[18px] text-v2-fg-3">
                    {o.description}
                  </p>
                )}
                {/* Below md the four numbers become one line. */}
                <p className="v2-num mt-1.5 text-v2-12 text-v2-fg-2 md:hidden">
                  <span className="font-medium text-v2-fg">
                    {formatRate(p.baseCents, p.currency, 'mo')}
                  </span>
                  {' · '}
                  {formatMinutes(p.includedMinutes)} included
                  {' · '}
                  {formatRate(p.overageCentsPerMinute, p.currency, 'min')} after
                  {p.setupFeeCents > 0 &&
                    ` · ${formatMoney(p.setupFeeCents, p.currency)} setup`}
                </p>
              </div>
              <dl className="hidden grid-cols-4 gap-x-6 text-right md:grid">
                <Num label="Monthly">
                  {formatMoney(p.baseCents, p.currency)}
                </Num>
                <Num label="Included">{formatMinutes(p.includedMinutes)}</Num>
                <Num label="After that">
                  {formatRate(p.overageCentsPerMinute, p.currency, 'min')}
                </Num>
                <Num label="Setup" muted={p.setupFeeCents === 0}>
                  {p.setupFeeCents > 0
                    ? formatMoney(p.setupFeeCents, p.currency)
                    : 'None'}
                </Num>
              </dl>
              <span
                aria-hidden
                className={clsx(
                  'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border',
                  selected
                    ? 'border-v2-accent bg-v2-accent text-v2-on-accent'
                    : 'border-v2-line-strong bg-v2-surface',
                )}
              >
                {selected && <IconCheck size={12} />}
              </span>
            </div>

            {selected && (
              <div className="v2-fade-in px-4 pb-4 pl-[calc(1rem+16px)]">
                <p className="v2-num text-v2-12 text-v2-fg-3">
                  Usage:{' '}
                  {formatRate(p.overageCentsPerMinute, p.currency, 'min')} after{' '}
                  {formatMinutes(p.includedMinutes)}
                  {p.perOrderCents > 0 &&
                    ` · ${formatRate(p.perOrderCents, p.currency, 'order')} per confirmed order`}
                  {p.trialDays
                    ? ` · billing starts after a ${p.trialDays}-day trial`
                    : ''}
                  .
                </p>

                {showAddons && (
                  <div className="mt-3">
                    <p className="text-v2-12 font-semibold text-v2-fg-3">
                      Add-ons
                    </p>
                    <ul className="mt-1 border-t border-v2-line/70">
                      {ADDON_KEYS.map((k) => {
                        const a = p.addons[k];
                        const checked = a.included || addons[k];
                        const locked = a.included || !!o.addonsLocked;
                        const id = `${labelId}-${k}`;
                        return (
                          <li
                            key={k}
                            className="flex items-center gap-3 border-b border-v2-line/70 py-2 last:border-b-0"
                          >
                            <input
                              id={id}
                              type="checkbox"
                              checked={checked}
                              disabled={locked}
                              title={
                                a.included
                                  ? 'Included in this plan'
                                  : o.addonsLocked
                              }
                              onChange={(e) =>
                                onAddonsChange({
                                  ...addons,
                                  [k]: e.target.checked,
                                })
                              }
                              className="h-4 w-4 shrink-0 rounded-v2-sm border-v2-line-strong text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0 disabled:opacity-60"
                            />
                            <label
                              htmlFor={id}
                              className={clsx(
                                'min-w-0 flex-1 text-v2-13',
                                locked ? 'cursor-default' : 'cursor-pointer',
                              )}
                            >
                              <span className="font-medium text-v2-fg">
                                {ADDON_META[k].label}
                              </span>
                              <span className="hidden text-v2-fg-3 sm:inline">
                                {' '}
                                · {ADDON_META[k].description}
                              </span>
                            </label>
                            <span className="v2-num shrink-0 text-v2-12">
                              {a.included ? (
                                <Tag>Included</Tag>
                              ) : (
                                <span
                                  className={
                                    checked ? 'text-v2-fg' : 'text-v2-fg-3'
                                  }
                                >
                                  +{formatRate(a.cents, p.currency, 'mo')}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {o.addonsLocked && (
                      <Reason className="mt-1.5">{o.addonsLocked}</Reason>
                    )}
                  </div>
                )}

                {o.features && o.features.length > 0 && (
                  <ul className="mt-3 grid gap-x-6 gap-y-1 text-v2-12 text-v2-fg-2 sm:grid-cols-2">
                    {o.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5">
                        <IconCheck
                          size={12}
                          className="mt-1 shrink-0 text-v2-fg-3"
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Num: React.FC<{
  label: string;
  children: React.ReactNode;
  muted?: boolean;
}> = ({ label, children, muted }) => (
  <div className="min-w-[72px]">
    <dt className="text-v2-11 text-v2-fg-3">{label}</dt>
    <dd
      className={clsx(
        'v2-num mt-0.5 whitespace-nowrap text-v2-13 font-medium',
        muted ? 'text-v2-fg-4' : 'text-v2-fg',
      )}
    >
      {children}
    </dd>
  </div>
);

/**
 * The plan's hue mark: a small rounded *square*, so it never reads as a
 * status dot (round and semantic) when the two share a line.
 */
export const PlanSwatch: React.FC<{ hue: Hue; className?: string }> = ({
  hue,
  className,
}) => (
  <span
    aria-hidden
    className={clsx(
      'inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]',
      HUE_FILL[hue],
      className,
    )}
  />
);

/** Swatch + plan name, for places that name the plan (Billing row, receipt). */
export const PlanName: React.FC<{
  code: string | null | undefined;
  name: React.ReactNode;
  hue?: Hue;
  size?: 'sm' | 'md';
  className?: string;
}> = ({ code, name, hue, size = 'md', className }) => (
  <span className={clsx('inline-flex items-center gap-2', className)}>
    <PlanSwatch hue={hue ?? planHue(code)} />
    <span
      className={clsx(
        'font-semibold text-v2-fg',
        size === 'md' ? 'text-v2-14 tracking-[-0.01em]' : 'text-v2-13',
      )}
    >
      {name}
    </span>
  </span>
);
